import { db } from '@/firebase';
import { Bill } from '@/types';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  getDoc,
  orderBy,
} from 'firebase/firestore';

const getBillsCollection = (tripId: string) => collection(db, 'trips', tripId, 'bills');

// Helper function to transform Firestore data to Bill
const transformFirestoreData = (doc: { id: string; data: () => Record<string, unknown> }): Bill => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    generatedAt: data.generatedAt?.toDate(),
    sentAt: data.sentAt?.toDate(),
    paidAt: data.paidAt?.toDate(),
    createdAt: data.createdAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
    events: (data.events || []).map((event: {
      id: string;
      type: string;
      timestamp: { toDate?: () => Date } | Date;
      triggeredBy?: string;
      triggeredByName?: string;
      details?: Record<string, unknown>;
    }) => ({
      ...event,
      timestamp: event.timestamp?.toDate ? event.timestamp.toDate() : new Date(event.timestamp)
    })),
    paymentTransactions: (data.paymentTransactions || []).map((transaction: {
      id: string;
      type: string;
      amount: number;
      timestamp: { toDate?: () => Date } | Date;
      createdAt: { toDate?: () => Date } | Date;
      updatedAt: { toDate?: () => Date } | Date;
      [key: string]: unknown;
    }) => ({
      ...transaction,
      timestamp: transaction.timestamp?.toDate ? transaction.timestamp.toDate() : new Date(transaction.timestamp),
      createdAt: transaction.createdAt?.toDate ? transaction.createdAt.toDate() : new Date(transaction.createdAt),
      updatedAt: transaction.updatedAt?.toDate ? transaction.updatedAt.toDate() : new Date(transaction.updatedAt)
    }))
  } as Bill;
};

export const listenToBills = (tripId: string, callback: (bills: Bill[]) => void): (() => void) => {
  const billsCollection = getBillsCollection(tripId);
  const q = query(billsCollection, orderBy('generatedAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const bills = snapshot.docs.map(transformFirestoreData);
    callback(bills);
  });
};

export const listenToBillsByApartment = (
  tripId: string, 
  apartmentId: string, 
  callback: (bills: Bill[]) => void
): (() => void) => {
  const billsCollection = getBillsCollection(tripId);
  const q = query(billsCollection, where('apartmentId', '==', apartmentId), orderBy('generatedAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const bills = snapshot.docs.map(transformFirestoreData);
    callback(bills);
  });
};

export const getBill = async (tripId: string, billId: string): Promise<Bill | null> => {
  const billDoc = doc(db, 'trips', tripId, 'bills', billId);
  const snapshot = await getDoc(billDoc);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return transformFirestoreData(snapshot);
};

// Helper function to remove undefined values from an object (Firebase doesn't accept undefined)
const removeUndefinedValues = (obj: Record<string, unknown>): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = {};
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value !== undefined) {
      if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        cleaned[key] = removeUndefinedValues(value as Record<string, unknown>);
      } else {
        cleaned[key] = value;
      }
    }
  });
  return cleaned;
};

export const addBill = async (tripId: string, bill: Omit<Bill, 'id'>): Promise<string> => {
  const billsCollection = getBillsCollection(tripId);
  
  // Clean the bill object to remove any undefined values
  const cleanBill = removeUndefinedValues({
    ...bill,
    events: bill.events || [],
    paymentTransactions: bill.paymentTransactions || [],
    createdAt: new Date(),
    updatedAt: new Date(),
  });
  
  const docRef = await addDoc(billsCollection, cleanBill);
  return docRef.id;
};

export const updateBill = async (
  tripId: string, 
  billId: string, 
  data: Partial<Bill>
): Promise<void> => {
  if (!billId) throw new Error("Bill ID is missing");
  const billDoc = doc(db, 'trips', tripId, 'bills', billId);
  await updateDoc(billDoc, {
    ...data,
    updatedAt: new Date(),
  });
};

export const deleteBill = async (tripId: string, billId: string): Promise<void> => {
  const billDoc = doc(db, 'trips', tripId, 'bills', billId);
  await deleteDoc(billDoc);
};
